import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export const PLANS = [
    { id: "standard", name: "Standard membership", price: "£12.50", per: "/month", note: "Billed monthly over a 12-month subscription" },
    { id: "student", name: "Student membership", price: "£6.50", per: "/month", note: "For current students. Billed monthly over a 12-month subscription" },
    { id: "standard_annual", name: "Standard membership — pay yearly", price: "£125", per: "/year", note: "One payment for 12 months — 2 months free", badge: "Best value" },
];

export const PRICE_SUMMARY = "Standard £12.50/month (or £125/year — 2 months free), Student £6.50/month";

export function openMembership() {
    window.dispatchEvent(new Event("open-membership"));
}

function PlanOption({ plan, selected, onSelect }) {
    return (
        <button type="button" onClick={() => onSelect(plan.id)} data-testid={`plan-option-${plan.id}`}
            className={`w-full text-left rounded-xl border-2 p-4 transition-colors ${selected ? "border-[#C58B2B] bg-[#FBF7F0]" : "border-[#eee3d0] hover:border-[#C58B2B]/50"}`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    {plan.badge && <span className="inline-block mb-1.5 rounded-full bg-[#C58B2B] text-white text-[11px] font-bold uppercase tracking-wider px-2.5 py-0.5" data-testid={`plan-badge-${plan.id}`}>{plan.badge}</span>}
                    <p className="font-display font-bold text-[#14203A]">{plan.name}</p>
                    <p className="text-xs text-[#5A6B7C] mt-1">{plan.note}</p>
                </div>
                <div className="text-right shrink-0">
                    <span className="font-display text-xl font-bold text-[#14203A]">{plan.price}</span>
                    <span className="text-xs text-[#5A6B7C]">{plan.per}</span>
                </div>
            </div>
            {selected && <CheckCircle2 className="h-4 w-4 text-[#00A499] mt-2" />}
        </button>
    );
}

export function MembershipChooser() {
    const [open, setOpen] = useState(false);
    const [plan, setPlan] = useState("standard");
    const [isStudent, setIsStudent] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const handler = () => { setOpen(true); };
        window.addEventListener("open-membership", handler);
        return () => { window.removeEventListener("open-membership", handler); };
    }, []);

    const blocked = plan === "student" && !isStudent;

    const checkout = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, plan, student_confirmed: isStudent });
            if (data?.checkout_url) { window.location.href = data.checkout_url; return; }
            toast.error("Could not start checkout. Please try again.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start checkout. Please try again.");
        }
        setLoading(false);
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-w-lg" data-testid="membership-chooser">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl text-[#14203A]">Choose your membership</DialogTitle>
                    <DialogDescription>Pre-register for a 12-month subscription. Membership is managed in the EDI Connect app from launch in November.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                    {PLANS.map((p) => <PlanOption key={p.id} plan={p} selected={plan === p.id} onSelect={setPlan} />)}
                </div>
                {plan === "student" && (
                    <label className="flex items-start gap-3 text-sm text-[#3d4657] mt-2 cursor-pointer" data-testid="student-confirm-label">
                        <Checkbox checked={isStudent} onCheckedChange={(v) => setIsStudent(v === true)} data-testid="student-confirm-checkbox" className="mt-0.5" />
                        I confirm I am currently a student.
                    </label>
                )}
                <Button size="lg" onClick={checkout} disabled={loading || blocked} data-testid="membership-checkout-btn"
                    className="mt-2 w-full bg-[#C58B2B] hover:bg-[#a9741f] text-white font-bold rounded-full gap-2">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue to secure payment <ArrowRight className="h-4 w-4" /></>}
                </Button>
            </DialogContent>
        </Dialog>
    );
}
